import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api.js';
import { Doodle, Float, Reveal, DecoDoodle } from '../Doodles.jsx';
import HeroVideo from '../HeroVideo.jsx';

/* Asocia un doodle a cada categoría según su slug/nombre */
function doodleFor(slug = '') {
  const s = slug.toLowerCase();
  if (s.includes('cookie') && s.includes('cake')) return 'cupcake';
  if (s.includes('cookie') || s.includes('galleta')) return 'cookie';
  if (s.includes('cheesecake') || s.includes('tres')) return 'drip';
  if (s.includes('tarta') || s.includes('cumple')) return 'candle';
  if (s.includes('caja') || s.includes('box') || s.includes('regalo')) return 'sprinkles';
  if (s.includes('dollie') || s.includes('bundt') || s.includes('cupcake')) return 'swirl';
  return 'cupcake';
}

export default function Home() {
  const [cats, setCats] = useState([]);
  const [destacados, setDestacados] = useState([]);
  const heroRef = useRef(null);

  useEffect(() => {
    api.categories().then(setCats).catch(() => {});
    api.products('?destacado=1').then(setDestacados).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO (pineado mientras se completa el glaseado con el scroll) */}
      <section className="hero" ref={heroRef}>
       <div className="hero-sticky">
        <DecoDoodle name="sparkle" size={42} top="14%" left="6%" dur={6} opacity={0.6} />
        <DecoDoodle name="drip" size={56} top="2%" right="14%" dur={7} delay={0.4} opacity={0.4} />
        <DecoDoodle name="heart" size={34} bottom="12%" left="10%" dur={5} delay={0.8} color="var(--rose)" opacity={0.7} />
        <div className="hero-inner">
          <Reveal className="hero-copy" y={24} now>
            <p className="kicker"><Doodle name="sparkle" size={20} color="var(--mint-d)" /> Repostería creativa · Madrid</p>
            <h1>Repostería que se <em>dibuja</em> con cariño.</h1>
            <p className="lead">
              Cookie cakes, cheesecakes y tartas personalizadas, hechas a mano en Madrid.
              Tú sueñas el sabor, nosotras lo horneamos.
            </p>
            <div className="hero-cta">
              <Link to="/disena-tu-cookie-cake" className="btn btn--primary">Diseña tu cookie cake</Link>
              <Link to="/carta" className="btn btn--ghost">Ver la carta</Link>
            </div>
            <Link to="/pedido-personalizado" className="hero-custom">
              <Doodle name="sparkle" size={18} color="var(--mint-d)" /> ¿Buscas algo único? Pide tu tarta personalizada →
            </Link>
          </Reveal>

          <Reveal className="hero-visual" delay={0.15} y={28} now>
            <HeroVideo
              track={heroRef}
              src="/brand/hero-glaze.mp4"
              poster="/brand/hero-glaze.jpg"
              className="hero-photo"
            />
            <Float amp={10} dur={5} className="deco" style={{ top: '-4%', right: '4%' }}>
              <Doodle name="cupcake" size={70} draw />
            </Float>
            <Float amp={8} dur={6} delay={0.6} className="deco" style={{ bottom: '0%', left: '-4%' }}>
              <Doodle name="cherry" size={54} draw color="var(--rose)" />
            </Float>
            <Float amp={6} dur={4.4} delay={1} className="deco" style={{ bottom: '24%', right: '-6%' }}>
              <Doodle name="sparkle" size={36} draw color="var(--butter)" />
            </Float>
          </Reveal>
        </div>
       </div>
      </section>

      {/* VALORES */}
      <section className="values">
        {[
          { d: 'whisk', t: 'Hecho con mimo', p: 'Cada cookie cake y cheesecake sale de nuestras manos, no de una máquina. Masa, horneado y decoración 100% artesanal.' },
          { d: 'sparkle', t: 'A tu gusto', p: 'Eliges tamaño, sabores, extras y hasta el texto. Tu pedido se hornea solo cuando lo confirmas. Fresco y personal.' },
          { d: 'heart', t: 'En Madrid, con amor', p: 'Recoge en el obrador o recíbelo donde estés. Te confirmamos cada paso como si fuera un regalo.' },
        ].map((v, i) => (
          <Reveal key={v.t} className="value" delay={i * 0.12} y={26}>
            <div className="v-ico"><Doodle name={v.d} size={60} draw color={v.d === 'heart' ? 'var(--rose)' : 'var(--plum)'} /></div>
            <h3>{v.t}</h3>
            <p>{v.p}</p>
          </Reveal>
        ))}
      </section>

      {/* CATEGORÍAS */}
      <section className="section">
        <Reveal className="section-head">
          <p className="kicker hand" style={{ fontSize: '1.3rem' }}>Escoge tu antojo</p>
          <h2>Nuestra carta</h2>
          <p>Elige una categoría y empieza tu pedido.</p>
        </Reveal>
        <div className="cat-grid">
          {cats.map((c, i) => (
            <Reveal key={c.slug} delay={(i % 3) * 0.1} y={28}>
              <Link to={`/carta?cat=${c.slug}`} className="cat-card">
                <div className="cat-img" style={{ backgroundImage: `url(${c.imagen})` }}>
                  <span className="cat-doodle"><Doodle name={doodleFor(c.slug + ' ' + c.nombre)} size={30} color="var(--plum)" /></span>
                </div>
                <div className="cat-meta"><h3>{c.nombre}</h3><p>{c.descripcion}</p></div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* DESTACADOS */}
      {destacados.length > 0 && (
        <section className="section section--cream">
          <DecoDoodle name="sprinkles" size={56} top="8%" right="8%" opacity={0.5} dur={6} />
          <DecoDoodle name="cookie" size={48} bottom="10%" left="6%" opacity={0.45} dur={7} delay={0.5} />
          <Reveal className="section-head">
            <p className="kicker hand" style={{ fontSize: '1.3rem' }}>Los más queridos</p>
            <h2>Lo que más triunfa en Kanel</h2>
          </Reveal>
          <div className="prod-grid">
            {destacados.map((p, i) => (
              <Reveal key={p.slug} delay={(i % 4) * 0.08} y={26}>
                <Link to={`/producto/${p.slug}`} className="prod-card">
                  <span className="tag-new">Recién horneado</span>
                  <div className="prod-img" style={{ backgroundImage: `url(${p.imagenes?.[0]})` }} />
                  <div className="prod-meta">
                    <h3>{p.nombre}</h3>
                    <span className="price">desde {p.precioBase} €</span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* CTA FINAL */}
      <section className="cta-band">
        <DecoDoodle name="drip" size={64} top="0" left="12%" color="rgba(255,255,255,.5)" opacity={1} draw={false} dur={6} />
        <DecoDoodle name="drip" size={54} top="0" right="18%" color="rgba(255,255,255,.4)" opacity={1} draw={false} dur={7} delay={0.6} />
        <Reveal>
          <p className="kicker hand" style={{ fontSize: '1.3rem' }}>¿Una celebración a la vista?</p>
          <h2>¿Lista para tu próximo capricho?</h2>
          <p>Cuéntanos tu idea y la hacemos realidad, dulce y a tu medida.</p>
          <Link to="/carta" className="btn btn--primary">Hacer un pedido</Link>
        </Reveal>
      </section>
    </div>
  );
}
